import { mdy, RELATIONSHIP_LABEL, requestRef, VOTE_WINDOW_LABEL, type ApprovalReview, type PendingDecision } from "@scheduler/shared";
import { useCallback, useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { dateRange, hours } from "../requests/format";
import { ProjectionLedger } from "../requests/ProjectionLedger";
import { sectionsFor } from "./sections";

/**
 * The approver's queue: pending PTO and no-travel requests the caller may decide. Field work is
 * not here: since 27 Oct 2026 a deployment is booked when saved and nobody approves it. The API
 * decides who sees what and who may act; this screen only shows it. An approval shows the
 * balance working, recomputed now — a shortfall is information for the approver, never a block.
 *
 * Two sections, not a filter (punch list S32, Jeremy, 3 Nov 2026): "As your supervisor" first,
 * then "As HR", so what comes to you as somebody's supervisor can be read without the rest.
 */
export function Approvals({ onCountChange }: { onCountChange: (n: number) => void }) {
  const api = useSchedulerApi();
  const [items, setItems] = useState<PendingDecision[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await api.pending();
      setItems(list);
      onCountChange(list.length);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the queue.");
    }
  }, [api, onCountChange]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => { void refresh(); }, [refresh, back]);

  /** Approve asks to confirm; deny has already been confirmed by writing the reason on the card. */
  async function decide(item: PendingDecision, action: "approve" | "deny", reason = "") {
    const name = `${headline(item)}, ${requestRef(item.label, item.start, item.end)}`;
    if (action === "approve" && !window.confirm(`Approve ${name}?`)) return;
    setError(null);
    try {
      const result = action === "approve" ? await api.approve(item.id) : await api.deny(item.id, reason);
      const replaced = item.replaces && result.changes.some((c) => c.reason === "SUPERSEDED")
        ? `; ${requestRef(item.label, item.replaces.start, item.replaces.end)} cancelled as replaced`
        : "";
      setMessage(`${action === "approve" ? "Approved" : "Denied"}: ${name}${replaced}.`);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `Could not ${action}.`);
      await refresh();
    }
  }

  return (
    <section aria-labelledby="approvals">
      <h2 id="approvals" className="panel-title">Waiting for your decision</h2>
      {message ? <p className="toast" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}
      {!items ? <p className="muted">Loading…</p> : items.length === 0 ? (
        <p className="muted">Nothing is waiting for you.</p>
      ) : (
        sectionsFor(items).map((section) => (
          <section key={section.decideAs} className="stack spaced-section" aria-labelledby={`approvals-${section.decideAs}`}>
            <h3 id={`approvals-${section.decideAs}`} className="panel-title">{section.title}</h3>
            {section.items.map((item) => <PendingCard key={item.id} item={item} onDecide={(a, reason) => decide(item, a, reason)} onChanged={() => void refresh()} />)}
          </section>
        ))
      )}
    </section>
  );
}

/** FIELD is named by its deployment; PTO and NTD by the person. */
const headline = (item: PendingDecision) =>
  item.type === "FIELD" ? item.title ?? "Field deployment" : item.person.name;

function PendingCard({ item, onDecide, onChanged }: {
  item: PendingDecision;
  onDecide: (action: "approve" | "deny", reason?: string) => Promise<void>;
  /** Something on the card changed (the summons tick): re-read the queue. */
  onChanged: () => void;
}) {
  const api = useSchedulerApi();
  const [review, setReview] = useState<ApprovalReview | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticking, setTicking] = useState(false);
  // Deny opens a reason box: required, and the person is told it (PR 9).
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function deny() {
    setBusy(true);
    try {
      await onDecide("deny", reason.trim());
    } finally {
      setBusy(false);
    }
  }

  /** H3: HR has seen the summons. No upload, no attachment: the tick is the record. */
  async function summons() {
    setTicking(true);
    setError(null);
    try {
      await api.summonsReceived(item.id);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not record the summons.");
    } finally {
      setTicking(false);
    }
  }

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !review && item.type === "PTO") {
      try {
        setReview(await api.review(item.id));
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Could not load the working.");
      }
    }
  }

  return (
    <article className="card card-pad pending" aria-label={`${headline(item)}: ${requestRef(item.label, item.start, item.end)}`}>
      <div className="pending-head">
        <span className={`pill ${item.type}`}>{item.label}</span>
        <strong>{headline(item)}</strong>
        <span className="num">{dateRange(item.start, item.end)}</span>
        {item.type === "PTO" ? <span className="num">{hours(item.chargedHours)} h</span> : null}
      </div>
      {item.type === "FIELD" ? <p className="hint">Crew: {item.people.map((p) => p.name).join(", ")}</p> : null}
      {item.type === "FIELD" ? (
        <p className="hint">
          {item.project ? `Project: ${item.project.name}` : "No project (booked before projects were required)"}
          {item.projectManager ? ` · PM: ${item.projectManager.name}` : " · No PM on record, so only HR can decide this"}
        </p>
      ) : null}
      <p className="hint">
        Submitted {mdy(item.submittedAt.slice(0, 10))}
        {item.type === "FIELD" || item.submittedBy.id !== item.person.id ? ` by ${item.submittedBy.name}` : ""}
        {item.replaces ? ` · replaces ${requestRef(item.label, item.replaces.start, item.replaces.end)} (${item.replaces.status.toLowerCase()}), which approving cancels` : ""}
      </p>
      {item.bereavementRelationship ? <p className="hint">Relationship: {RELATIONSHIP_LABEL[item.bereavementRelationship]}</p> : null}
      {item.voteWindow ? <p className="hint">When: {VOTE_WINDOW_LABEL[item.voteWindow]}</p> : null}
      {item.timeOffKind === "JURY_DUTY" && item.decideAs === "HR" ? (
        item.summonsReceived ? (
          <p className="hint">Summons received, ticked by {item.summonsReceived.by.name} on {mdy(item.summonsReceived.at.slice(0, 10))}.</p>
        ) : (
          <label className="togglerow">
            <input type="checkbox" checked={false} disabled={ticking} onChange={() => void summons()} />
            <span>
              <span className="tt">Summons received</span>
              <span className="ts">Tick once you have seen it. Nothing is uploaded or kept.</span>
            </span>
          </label>
        )
      ) : null}
      {!open && error ? <p className="error">{error}</p> : null}
      {item.explanation ? <p className="explain"><strong>Explanation:</strong> {item.explanation}</p> : null}
      {item.lockedThrough && !item.canApprove ? (
        <p className="flag"><strong>Locked:</strong> payroll has processed PTO through {mdy(item.lockedThrough)}. Only HR can approve this; you can still deny it.</p>
      ) : item.lockedThrough ? (
        <p className="flag"><strong>Locked:</strong> payroll has processed PTO through {mdy(item.lockedThrough)}. Approving as HR is recorded in the admin log.</p>
      ) : null}

      {item.type === "PTO" ? (
        <button type="button" className="ghost" aria-expanded={open} onClick={() => void toggle()}>
          {open ? "Hide the working" : "Show the working"}
        </button>
      ) : null}
      {open && item.type === "PTO" ? (
        <div className="calc review">
          <div className="calc-body">
            {error ? <p className="error">{error}</p> : null}
            {review ? (
              <>
                <ProjectionLedger projection={review.projection} requested={review.requested} />
                {review.shortBy !== null ? (
                  <p className="flag"><strong>Short by {hours(review.shortBy)} h</strong> on current numbers. You can still approve; it is your call.</p>
                ) : null}
              </>
            ) : !error ? <p className="muted">Recomputing…</p> : null}
          </div>
        </div>
      ) : null}

      {denying ? (
        <div className="field denybox">
          <label className="label" htmlFor={`deny-${item.id}`}>Why are you denying it?</label>
          <textarea id={`deny-${item.id}`} rows={2} maxLength={500} value={reason} aria-describedby={`deny-hint-${item.id}`}
            onChange={(e) => setReason(e.target.value)} />
          <span id={`deny-hint-${item.id}`} className="hint">Required. {item.type === "FIELD" ? "The crew" : item.person.name} will see this reason.</span>
        </div>
      ) : null}
      <div className="acts">
        {denying ? (
          <>
            <button type="button" className="ghost no" disabled={!reason.trim() || busy} onClick={() => void deny()}>Deny</button>
            <button type="button" className="ghost" onClick={() => { setDenying(false); setReason(""); }}>Back</button>
          </>
        ) : (
          <>
            <button type="button" className="ghost go" disabled={!item.canApprove} onClick={() => void onDecide("approve")}>Approve</button>
            <button type="button" className="ghost no" onClick={() => setDenying(true)}>Deny…</button>
          </>
        )}
      </div>
    </article>
  );
}
